/**
 * ResetPassword — user lands here from the email link with ?token=...
 *
 * Flow:
 *   1) On mount, validate the token via /api/auth/reset-password/validate.
 *      - not_found / used / expired — show a helpful message + link back.
 *      - valid — reveal the new-password form.
 *   2) On submit, POST /api/auth/reset-password/complete which returns a
 *      fresh JWT. We stash the token, persist the email for encryption-key
 *      derivation, then navigate to /dashboard — auto-login.
 *
 * Because the encryption key is derived from the account email (see
 * `utils/encryption.js`), the user's existing encrypted downloads remain
 * fully readable after the reset. No wipe / no recovery phrase needed.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { api } from '../utils/api';
import Disclaimer from '../components/Disclaimer';

const FRIENDLY_REASON = {
  not_found:
    "This reset link isn't valid. Please request a new one from the sign-in page.",
  used:
    'This reset link has already been used. If you still need to change your password, request a new link.',
  expired:
    'This reset link has expired. Reset links are only valid for 60 minutes for your security — please request a new one.',
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [invalidReason, setInvalidReason] = useState(null);
  const [accountEmail, setAccountEmail] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 1) Validate the token as soon as we mount
  useEffect(() => {
    let cancelled = false;
    const validate = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        setInvalidReason('not_found');
        return;
      }
      try {
        const res = await api.validateResetToken(token);
        if (cancelled) return;
        if (res?.valid) {
          setTokenValid(true);
          setAccountEmail(res.email || '');
        } else {
          setTokenValid(false);
          setInvalidReason(res?.reason || 'not_found');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Token validation failed', err);
        setTokenValid(false);
        setInvalidReason('not_found');
      } finally {
        if (!cancelled) setValidating(false);
      }
    };
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const passwordStrengthOk = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordStrengthOk) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!passwordsMatch) {
      toast.error('Passwords don’t match');
      return;
    }
    setSubmitting(true);
    try {
      await api.completePasswordReset(token, newPassword, accountEmail);
      toast.success('Password updated', {
        description: 'You’re signed in — welcome back.',
      });
      navigate('/dashboard');
    } catch (err) {
      console.error('Password reset failed', err);
      toast.error(err?.message || 'Reset failed', {
        description:
          'This link may have expired between validation and now. Please request a fresh reset link.',
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--sd-bg)] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(circle at 15% 20%, rgba(212,175,55,0.10) 0%, transparent 45%), radial-gradient(circle at 90% 85%, rgba(183,110,121,0.12) 0%, transparent 55%)',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-md"
        >
          <div className="mb-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)] transition-colors"
              data-testid="reset-password-back-link"
            >
              <ArrowLeft size={14} />
              Back to sign in
            </Link>
          </div>

          <Card className="glass-card p-8" data-testid="reset-password-card">
            {validating ? (
              <div className="flex flex-col items-center gap-3 py-8 text-[var(--sd-muted-fg)]">
                <Loader2 size={24} className="animate-spin" />
                <p className="text-sm">Verifying your reset link…</p>
              </div>
            ) : !tokenValid ? (
              /* ─────── Invalid / expired / used ─────── */
              <div className="text-center space-y-4" data-testid="reset-password-invalid-state">
                <div
                  className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(220, 111, 111, 0.10)',
                    border: '1px solid rgba(220, 111, 111, 0.30)',
                  }}
                >
                  <XCircle size={22} className="text-[#DC6F6F]" />
                </div>
                <h1
                  className="text-xl font-semibold text-[var(--sd-fg)]"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  This link can&apos;t be used
                </h1>
                <p className="text-sm text-[var(--sd-muted-fg)] leading-relaxed">
                  {FRIENDLY_REASON[invalidReason] || FRIENDLY_REASON.not_found}
                </p>
                <div className="pt-2">
                  <Button
                    onClick={() => navigate('/forgot-password')}
                    className="w-full bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold"
                    data-testid="reset-password-request-new-link"
                  >
                    Request a new link
                    <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </div>
              </div>
            ) : (
              /* ─────── Valid token — show the new-password form ─────── */
              <>
                <div className="text-center mb-6">
                  <div
                    className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center"
                    style={{
                      background: 'rgba(212, 175, 55, 0.10)',
                      border: '1px solid rgba(212, 175, 55, 0.30)',
                    }}
                  >
                    <ShieldCheck size={22} className="text-[#D2B48C]" />
                  </div>
                  <h1
                    className="text-2xl font-semibold text-[var(--sd-fg)]"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    Set a new password
                  </h1>
                  {accountEmail && (
                    <p className="mt-2 text-sm text-[var(--sd-muted-fg)]">
                      for{' '}
                      <span className="text-[var(--sd-fg-secondary)] font-medium">
                        {accountEmail}
                      </span>
                    </p>
                  )}
                  <p className="mt-3 text-xs text-[var(--sd-muted-fg)] italic leading-relaxed">
                    Your existing encrypted downloads will remain fully readable after the reset.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="new-password"
                      className="block text-xs uppercase tracking-widest text-[var(--sd-muted-fg)] mb-2"
                    >
                      New password
                    </label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="h-11 pr-11 bg-[var(--sd-surface)] border-[var(--sd-border)] text-[var(--sd-fg)] placeholder:text-[var(--sd-muted-fg)]"
                        data-testid="reset-password-new-input"
                        disabled={submitting}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)] transition-colors"
                        data-testid="reset-password-toggle-visibility"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {newPassword.length > 0 && !passwordStrengthOk && (
                      <p className="mt-1.5 text-[11px] text-[#DC6F6F]">
                        Password must be at least 8 characters.
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="confirm-password"
                      className="block text-xs uppercase tracking-widest text-[var(--sd-muted-fg)] mb-2"
                    >
                      Confirm password
                    </label>
                    <Input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                      className="h-11 bg-[var(--sd-surface)] border-[var(--sd-border)] text-[var(--sd-fg)] placeholder:text-[var(--sd-muted-fg)]"
                      data-testid="reset-password-confirm-input"
                      disabled={submitting}
                    />
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <p className="mt-1.5 text-[11px] text-[#DC6F6F]">
                        Passwords don&apos;t match.
                      </p>
                    )}
                    {passwordsMatch && passwordStrengthOk && (
                      <p className="mt-1.5 text-[11px] text-[#7EBF7A] flex items-center gap-1">
                        <CheckCircle2 size={12} /> Looks good.
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting || !passwordStrengthOk || !passwordsMatch}
                    className="w-full h-11 bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold rounded-xl"
                    data-testid="reset-password-submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Updating…
                      </>
                    ) : (
                      <>Update password &amp; sign in</>
                    )}
                  </Button>
                </form>
              </>
            )}
          </Card>
        </motion.div>
      </div>

      <Disclaimer />
    </div>
  );
};

export default ResetPassword;
